import { useState, useEffect, useCallback } from "react";
import type { ChatSession } from "../types/index.js";
import { apiGet, apiPost, apiDelete } from "../lib/api.js";

interface UseChatSessionsReturn {
	sessions: ChatSession[];
	activeSessionId: string | null;
	loading: boolean;
	createSession: (projectId: string | null) => Promise<string>;
	selectSession: (id: string | null) => void;
	deleteSession: (id: string) => Promise<void>;
	refreshSessions: () => void;
}

/**
 * Chat sessions hook.
 *
 * Manages the list of chat sessions for the currently selected project.
 * When selectedProjectId changes, fetches sessions for that project and
 * clears the active session (ready for a new conversation).
 *
 * selectSession(null) means "new chat" — no session is active.
 */
export function useChatSessions(selectedProjectId: string | null): UseChatSessionsReturn {
	const [sessions, setSessions] = useState<ChatSession[]>([]);
	const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);

	// Fetch sessions whenever the selected project changes
	useEffect(() => {
		// Clear active session and sessions when project changes
		setActiveSessionId(null);
		setSessions([]);

		if (!selectedProjectId) return;

		let cancelled = false;

		async function fetchSessions(): Promise<void> {
			setLoading(true);

			try {
				const response = await apiGet(`/api/chat/sessions?projectId=${selectedProjectId}`);

				if (cancelled) return;

				if (response.ok) {
					const data = await response.json() as { sessions: ChatSession[] };
					setSessions(data.sessions);
				}
				// Silently fail — sessions are non-critical; user can still chat
			} catch {
				// Network errors silently ignored; sessions panel shows empty state
			} finally {
				if (!cancelled) {
					setLoading(false);
				}
			}
		}

		fetchSessions();

		return () => {
			cancelled = true;
		};
	}, [selectedProjectId]);

	/**
	 * Re-fetches the session list for the current project.
	 * Called by useChat after saving a message pair.
	 */
	const refreshSessions = useCallback(() => {
		if (!selectedProjectId) return;

		async function refetch(): Promise<void> {
			try {
				const response = await apiGet(`/api/chat/sessions?projectId=${selectedProjectId}`);
				if (response.ok) {
					const data = await response.json() as { sessions: ChatSession[] };
					setSessions(data.sessions);
				}
			} catch {
				// Silently ignore refresh failures
			}
		}

		refetch();
	}, [selectedProjectId]);

	/**
	 * Creates a new session for the given project and returns its ID.
	 * Adds the new session to the top of the session list.
	 */
	const createSession = useCallback(async (projectId: string | null): Promise<string> => {
		const response = await apiPost("/api/chat/sessions", {
			projectId: projectId ?? undefined,
		});

		if (!response.ok) {
			throw new Error(`Failed to create chat session (${response.status})`);
		}

		const data = await response.json() as { session: ChatSession };
		const newSession = data.session;

		setSessions((prev) => [newSession, ...prev]);
		setActiveSessionId(newSession.id);

		return newSession.id;
	}, []);

	/**
	 * Sets the active session. Passing null starts a new chat with no active session.
	 */
	const selectSession = useCallback((id: string | null) => {
		setActiveSessionId(id);
	}, []);

	/**
	 * Deletes a session by ID and removes it from the list.
	 * If the deleted session was active, clears the active session.
	 */
	const deleteSession = useCallback(async (id: string): Promise<void> => {
		const response = await apiDelete(`/api/chat/sessions/${id}`);

		if (!response.ok && response.status !== 404) {
			throw new Error(`Failed to delete session (${response.status})`);
		}

		setSessions((prev) => prev.filter((s) => s.id !== id));
		setActiveSessionId((current) => (current === id ? null : current));
	}, []);

	return {
		sessions,
		activeSessionId,
		loading,
		createSession,
		selectSession,
		deleteSession,
		refreshSessions,
	};
}
